import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import './ServiceDetail.css';
import softwareImg from '../images/services/software-engineering.png';
import webImg from '../images/services/web-development.png';
import mobileImg from '../images/services/mobile-applications.png';
import aiImg from '../images/services/ai-automation.png';
import digitalImg from '../images/services/digital-transformation.png';
import securityImg from '../images/services/cybersecurity.png';
// Using professional placeholders for the remaining 2 due to transient quota
import cloudImg from '../images/programming coding background.jpg';
import systemsImg from '../images/image agreeding on deal.jpg';

const servicesContent = {
    "software-engineering": {
        title: "Software Engineering",
        subtitle: "Enterprise-grade architectures for modern businesses.",
        description: "At Kodebuild, we don't just write code; we engineer comprehensive systems designed to be the backbone of your business growth. Our engineering philosophy centers on 'Performance, Scalability, and Security.' We specialize in building robust backend architectures that can handle high-concurrency traffic and complex business logic without breaking a sweat.",
        extendedDescription: "Our approach goes beyond the initial build. We consider the total cost of ownership and the long-term maintainability of every system we create. By leveraging microservices where appropriate and modular monoliths where efficiency is key, we ensure that your software can evolve as your business pivots. Our engineers are experts in modern tech stacks, including Node.js, Python, and Go, ensuring your infrastructure is built on the most reliable foundations available today.",
        features: [
            "Microservices & Cloud-Native Architecture",
            "High-Performance API Development",
            "Legacy System Modernization",
            "DevOps & Continuous Integration",
            "Automated Testing & Quality Assurance",
            "Scalable Database Design"
        ],
        image: softwareImg
    },
    "web-development": {
        title: "Web Development",
        subtitle: "Modern, responsive, and high-converting websites.",
        description: "In the digital-first era, your website is your most valuable employee. We build high-performance, responsive web applications that do more than just look pretty—they convert visitors into loyal customers. Using cutting-edge frameworks like React and Next.js, we deliver sub-second load times and seamless transitions that keep users engaged.",
        extendedDescription: "We take a mobile-first approach to every web project, ensuring a flawless experience across all devices. Our web development process includes deep SEO integration from day one, optimized asset delivery via global CDNs, and robust state management for complex functional requirements. Whether you need a sophisticated headless CMS setup or a highly interactive dashboard, our web solutions are designed for speed, accessibility, and measurable ROI.",
        features: [
            "Progressive Web Apps (PWA)",
            "Headless CMS Integration",
            "E-commerce Development",
            "SEO & Performance Optimization",
            "Responsive & Mobile-First Design",
            "Custom Web Applications"
        ],
        image: webImg
    },
    "mobile-applications": {
        title: "Mobile Applications",
        subtitle: "Native and cross-platform mobile solutions.",
        description: "Connect with your audience in their pockets. We build intuitive, high-performance mobile applications that users love to open. Whether you're targeting iOS, Android, or both, our mobile solutions are engineered for stability and speed.",
        extendedDescription: "We specialize in React Native and Flutter, allowing us to deliver near-native performance while significantly reducing development time and costs for our clients. For projects requiring the absolute limit of hardware performance, we offer full native Swift and Kotlin development. Our mobile apps include advanced features like offline synchronization, biometric authentication, and complex push notification logic, all wrapped in a world-class UI/UX design.",
        features: [
            "iOS & Android Native Development",
            "React Native & Flutter Solutions",
            "Mobile App UI/UX Design",
            "App Store Optimization (ASO)",
            "Push Notification Integration",
            "Offline Mode Functionality"
        ],
        image: mobileImg
    },
    "ai-automation": {
        title: "AI & Automation",
        subtitle: "Intelligent systems tailored for efficiency.",
        description: "Transform your data into your most powerful asset. We help businesses leverage the latest advancements in Artificial Intelligence and Machine Learning to automate repetitive tasks and gain deep, actionable insights into their operations.",
        extendedDescription: "From implementing custom LLM-powered agents to building predictive models for inventory management, our AI solutions are focused on practical, high-impact applications. We help you move beyond simple automation to truly 'intelligent' systems that can analyze trends, predict customer behavior, and optimize your supply chain in real-time. Our goal is to augment your human workforce, freeing them from mundane tasks to focus on strategy and creativity.",
        features: [
            "Custom LLM & Chatbot Implementation",
            "Process Automation (RPA)",
            "Predictive Analytics & Modelling",
            "Computer Vision Solutions",
            "Natural Language Processing",
            "AI-Driven Data Insights"
        ],
        image: aiImg
    },
    "digital-transformation": {
        title: "Digital Transformation",
        subtitle: "Modernize your brand for the digital age.",
        description: "Digital transformation is not just about adopting new tools; it's about a fundamental shift in how you deliver value to your customers. We provide the strategic roadmap and the technical muscle to modernize your entire operation.",
        extendedDescription: "We work closely with leadership teams to identify digital bottlenecks and untapped opportunities. Our transformation services include core system modernization, data migration to secure cloud environments, and the implementation of unified customer experience platforms. We help you build a culture of innovation by providing the stable, scalable digital foundations necessary for long-term agility in a rapidly changing market.",
        features: [
            "Comprehensive Digital Strategy",
            "User Experience (UX) Redesign",
            "Data Migration & Strategy",
            "Mobile-First Experiences",
            "Change Management Consulting",
            "Tech Stack Audit & Optimization"
        ],
        image: digitalImg
    },
    "cybersecurity": {
        title: "Cybersecurity",
        subtitle: "Proactive protection for your digital assets.",
        description: "In an era of sophisticated cyber-attacks, security can no longer be an afterthought. We provide comprehensive protection for your data, your infrastructure, and your brand's reputation.",
        extendedDescription: "Our cybersecurity services range from initial vulnerability assessments and penetration testing to the design and implementation of mature zero-trust architectures. We help you protect sensitive customer data and intellectual property through advanced encryption, multi-factor authentication systems, and real-time threat detection. We don't just build walls; we build resilient systems that can detect and recover from incidents with minimal business disruption.",
        features: [
            "Penetration Testing & Audits",
            "Zero-Trust Architecture Setup",
            "Threat Detection & Response",
            "Data Encryption & Privacy",
            "Cloud Security Hardening",
            "Employee Security Training"
        ],
        image: securityImg
    },
    "cloud-infrastructure": {
        title: "Cloud Infrastructure",
        subtitle: "Scalable, secure, and cost-efficient cloud solutions.",
        description: "Modern businesses run on the cloud, but poorly managed cloud infrastructure can be a major cost center. We design and manage cloud environments that are optimized for both performance and profitability.",
        extendedDescription: "Our cloud experts specialize in AWS, Google Cloud, and Azure. We help you migrate from local servers or optimize your existing cloud presence using serverless architectures, containerization (Docker/Kubernetes), and Infrastructure-as-Code (Terraform). We focus on high availability, disaster recovery planning, and radical cost optimization, ensuring you only pay for the resources you actually need while maintaining the ability to scale instantly.",
        features: [
            "AWS, Azure & GCP Management",
            "Serverless Architecture Design",
            "Cloud Costs Optimization",
            "Disaster Recovery Planning",
            "Infrastructure as Code (Terraform)",
            "Containerization (Docker & K8s)"
        ],
        image: cloudImg
    },
    "systems-integration": {
        title: "Systems Integration",
        subtitle: "Connecting your software for a unified workflow.",
        description: "Siloed data is the enemy of efficiency. We specialize in building the bridges between your different software platforms, creating a unified, automated, and seamless digital nervous system for your business.",
        extendedDescription: "Whether it's connecting your ERP to your CRM, integrating third-party payment gateways, or building custom middleware to sync legacy systems with modern mobile apps, we ensure data flows securely and reliably. Our integration solutions eliminate manual data entry, reduce human error, and provide leadership with a 'single source of truth' for all business metrics. We make your software stack work together as one cohesive unit.",
        features: [
            "Custom API & Middleware Dev",
            "ERP & CRM Integrations",
            "Legacy to Cloud Connectivity",
            "Real-time Data Syncing",
            "Third-party Service Integration",
            "Secure Data Transformation"
        ],
        image: systemsImg
    }
};

const ServiceDetail = () => {
    const { id } = useParams();
    const service = servicesContent[id];

    if (!service) {
        return <div className="container">Service not found.</div>;
    }

    return (
        <motion.div
            className="service-detail-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            <Helmet>
                <title>{service.title} | Kodebuild</title>
                <meta name="description" content={service.subtitle} />
                <link rel="canonical" href={`https://kodebuildtech.com/service/${id}`} />
            </Helmet>
            <div className="container">
                <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <Link to="/" className="back-link">
                        <ArrowLeft size={20} /> Back to Home
                    </Link>
                </motion.div>

                <div className="service-hero">
                    <motion.div
                        className="service-info"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.6 }}
                    >
                        <span className="section-badge">Service Details</span>
                        <h1 className="service-title">{service.title}</h1>
                        <p className="service-subtitle">{service.subtitle}</p>
                        <p className="service-description">{service.description}</p>
                        <p className="service-description">{service.extendedDescription}</p>

                        <motion.div
                            className="feature-list"
                            initial="hidden"
                            animate="visible"
                            variants={{
                                visible: { transition: { staggerChildren: 0.1 } }
                            }}
                        >
                            {service.features.map((feature, index) => (
                                <motion.div
                                    key={index}
                                    className="feature-item"
                                    variants={{
                                        hidden: { opacity: 0, x: -10 },
                                        visible: { opacity: 1, x: 0 }
                                    }}
                                >
                                    <CheckCircle2 className="feature-icon" size={20} />
                                    <span>{feature}</span>
                                </motion.div>
                            ))}
                        </motion.div>
                    </motion.div>
                    <motion.div
                        className="service-visual"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                    >
                        <img src={service.image} alt={service.title} className="service-image" />
                    </motion.div>
                </div>
            </div>
        </motion.div>
    );
};

export default ServiceDetail;
